/*
 * The MAR chart: one row per medicine and time, one column per day, exactly as a paper chart
 * reads. It prints on A4 landscape, which is what a manager or an inspector will ask for.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';

type Cell = {
  status: 'given' | 'not-given' | 'due' | 'overdue' | 'upcoming' | 'none';
  code: string | null;
  initials: string;
  note: string;
  corrected: boolean;
};

type Row = {
  medicineId: string;
  name: string;
  doseText: string;
  directions: string;
  time: string;
  controlledDrug: boolean;
  timeCritical: boolean;
  cells: Record<string, Cell>;
};

type PrnLine = {
  date: string;
  at: string;
  medicine: string;
  reason: string;
  outcome: 'given' | 'not_given';
  code: string | null;
  codeLabel: string;
  initials: string;
  note: string;
  worked: boolean | null;
};

type ChartData = {
  home: string;
  resident: { name: string; room: string; dateOfBirth: string; allergies: string[] };
  from: string;
  days: number;
  cycle: { number: number; startsOn: string; endsOn: string; day: number | null; days: number } | null;
  dates: string[];
  rows: Row[];
  prn: PrnLine[];
  codes: Record<string, string>;
};

export function ChartScreen({ residentId, onBack }: { residentId: string; onBack: () => void }) {
  const [days, setDays] = useState(7);
  const [from, setFrom] = useState<string | null>(null);
  const [wholeCycle, setWholeCycle] = useState(false);
  const [chart, setChart] = useState<ChartData | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const query = new URLSearchParams({ days: String(days) });
      if (wholeCycle) query.set('cycle', from ?? 'current');
      else if (from) query.set('from', from);
      const data = await api.get<ChartData>(`/api/residents/${residentId}/chart?${query.toString()}`);
      setChart(data);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the chart.');
    }
  }, [residentId, days, from, wholeCycle]);

  useEffect(() => {
    void load();
  }, [load]);

  const shift = (by: number) => {
    const start = chart?.from ?? from;
    if (!start) return;
    const when = new Date(`${start}T12:00:00`);
    when.setDate(when.getDate() + by * days);
    setFrom(iso(when));
  };

  return (
    <section className="chart-screen">
      <div className="head no-print">
        <div>
          <h1>MAR chart</h1>
          <p className="muted small">{chart?.resident.name}{chart?.resident.room ? ` · Room ${chart.resident.room}` : ''}</p>
        </div>
        <button type="button" className="quiet" onClick={onBack}>Back</button>
      </div>

      <nav className="tabs no-print">
        <button type="button" className={wholeCycle ? 'on' : ''} onClick={() => { setWholeCycle(true); setFrom(null); }}>
          This cycle
        </button>
        <button type="button" className={!wholeCycle && days === 7 ? 'on' : ''}
          onClick={() => { setWholeCycle(false); setDays(7); }}>7 days</button>
        <button type="button" className={!wholeCycle && days === 28 ? 'on' : ''}
          onClick={() => { setWholeCycle(false); setDays(28); }}>28 days</button>
        <button type="button" onClick={() => shift(-1)}>← Earlier</button>
        <button type="button" onClick={() => shift(1)}>Later →</button>
        <button type="button" onClick={() => window.print()}>Print</button>
      </nav>

      {error ? <p className="error" role="alert">{error}</p> : null}
      {!chart ? <p className="muted">Loading…</p> : null}

      {chart ? (
        <>
          <div className="chart-header">
            <div>
              <h2>{chart.resident.name}</h2>
              <p className="muted small">
                {chart.home}
                {chart.resident.room ? ` · Room ${chart.resident.room}` : ''}
                {chart.resident.dateOfBirth ? ` · born ${formatDate(chart.resident.dateOfBirth)}` : ''}
              </p>
            </div>
            <p className="muted small">
              {formatDate(chart.from)} to {formatDate(chart.dates[chart.dates.length - 1] ?? chart.from)}
              {chart.cycle ? (
                <>
                  <br />
                  Cycle {chart.cycle.number}
                  {chart.cycle.day ? `, day ${chart.cycle.day} of ${chart.cycle.days}` : ''}
                </>
              ) : null}
            </p>
          </div>

          {chart.resident.allergies.length ? (
            <p className="notice warn">Allergies: {chart.resident.allergies.join(', ')}</p>
          ) : (
            <p className="muted small">No allergies recorded.</p>
          )}

          <div className="scroll">
            <table className="mar">
              <thead>
                <tr>
                  <th className="med">Medicine</th>
                  <th className="at">Time</th>
                  {chart.dates.map((date) => (
                    <th key={date} className="day">
                      <span>{dayName(date)}</span>
                      <span className="muted small">{shortDate(date)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chart.rows.map((row) => (
                  <tr key={`${row.medicineId}|${row.time}`}>
                    <th className="med" scope="row">
                      {row.name}
                      <small>
                        {row.doseText}
                        {row.controlledDrug ? ' · controlled drug' : ''}
                        {row.timeCritical ? ' · time critical' : ''}
                      </small>
                    </th>
                    <td className="at">{row.time}</td>
                    {chart.dates.map((date) => {
                      const cell = row.cells[date];
                      return (
                        <td key={date} className={`box ${cell?.status ?? 'none'}`} title={cellTitle(cell)}>
                          {cellText(cell)}
                          {cell?.corrected ? <sup>c</sup> : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!chart.rows.length ? <p className="muted">Nothing was due in these days.</p> : null}

          <h3>When required, and what happened</h3>
          {chart.prn.length ? (
            <div className="scroll">
              <table className="register">
                <thead>
                  <tr>
                    <th>When</th><th>Medicine</th><th>Why</th><th>What happened</th><th>Did it help?</th><th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {chart.prn.map((line, index) => (
                    <tr key={`${line.date}-${line.at}-${index}`}>
                      <td>{shortDate(line.date)} {line.at}</td>
                      <td>{line.medicine}</td>
                      <td>{line.reason}{line.note ? <small>{line.note}</small> : null}</td>
                      <td>{line.outcome === 'given' ? 'Given' : `Not given: ${line.codeLabel}`}</td>
                      <td>{line.worked === null ? '—' : line.worked ? 'Yes' : 'No'}</td>
                      <td>{line.initials}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted">None in these days.</p>
          )}

          <div className="legend">
            <strong>Codes</strong>
            {Object.entries(chart.codes).map(([letter, label]) => (
              <span key={letter} className="chip">{letter} {label}</span>
            ))}
            <span className="chip">✓ Given</span>
            <span className="chip">c Corrected, see the record</span>
          </div>
        </>
      ) : null}
    </section>
  );
}

function cellText(cell: Cell | undefined): string {
  if (!cell) return '';
  if (cell.status === 'given') return '✓';
  if (cell.status === 'not-given') return cell.code ?? '·';
  if (cell.status === 'overdue') return '!';
  return '';
}

function cellTitle(cell: Cell | undefined): string {
  if (!cell) return 'Not due';
  if (cell.status === 'given') return `Given by ${cell.initials}${cell.note ? ` · ${cell.note}` : ''}`;
  if (cell.status === 'not-given') return `Not given (${cell.code}) by ${cell.initials}${cell.note ? ` · ${cell.note}` : ''}`;
  if (cell.status === 'overdue') return 'Nothing recorded';
  return 'Still to come';
}

function iso(when: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`;
}

function dayName(date: string): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short' });
}

function shortDate(date: string): string {
  const [, m, d] = date.split('-');
  return `${d}/${m}`;
}

function formatDate(date: string): string {
  const [y, m, d] = date.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}
